import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Users, Truck, Loader2, FileText, Plus, Upload, ScanLine } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { ProductThumb } from "@/components/common/ProductThumb";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Command, CommandInput, CommandList, CommandGroup, CommandItem,
} from "@/components/ui/command";

const ICONS = { product: Package, customer: Users, supplier: Truck };
const CMDK_STYLES =
  "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-item]]:px-3 [&_[cmdk-item]]:py-2.5";

export default function CommandPalette({ open, onOpenChange }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const QUICK = [
    { key: "new_quote", to: "/quotes/new", perm: "quotes:write", icon: FileText, label: t("quotes.new") },
    { key: "new_customer", to: "/customers/new", perm: "customers:write", icon: Users, label: t("customers.new") },
    { key: "new_product", to: "/products/new", perm: "products:write", icon: Package, label: t("products.new") },
    { key: "import", to: "/imports", perm: "products:write", icon: Upload, label: t("import.title") },
    { key: "scan", to: "/scan", perm: "inventory:read", icon: ScanLine, label: t("scan.title") },
  ];

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open, onOpenChange]);

  useEffect(() => { if (!open) { setQ(""); setGroups([]); setLoading(false); } }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setGroups([]); setLoading(false); return; }
    setLoading(true);
    const id = ++reqId.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: term, limit: 6 } });
        if (id === reqId.current) setGroups(data.groups || []);
      } catch {
        if (id === reqId.current) setGroups([]);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 180);
    return () => clearTimeout(timer);
  }, [q]);

  const go = useCallback((url) => { onOpenChange(false); navigate(url); }, [navigate, onOpenChange]);
  const term = q.trim();
  const quickActions = QUICK.filter((a) => can(a.perm)).filter((a) => !term || a.label.toLowerCase().includes(term.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0" data-testid="command-palette">
        <DialogTitle className="sr-only">{t("search.placeholder")}</DialogTitle>
        <DialogDescription className="sr-only">{t("search.hint")}</DialogDescription>
        <Command shouldFilter={false} className={CMDK_STYLES}>
          <CommandInput
            autoFocus
            value={q}
            onValueChange={setQ}
            placeholder={t("search.placeholder")}
            data-testid="command-palette-input"
          />
          <CommandList className="max-h-[60vh]">
            {loading && (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("common.loading")}
              </div>
            )}
            {!loading && quickActions.length > 0 && (
              <CommandGroup heading={t("search.actions")}>
                {quickActions.map((a) => (
                  <CommandItem key={a.key} value={`action-${a.key}`} onSelect={() => go(a.to)} data-testid={`quick-action-${a.key}`} className="flex items-center gap-3">
                    <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 dark:bg-slate-800"><Plus className="h-3.5 w-3.5" /></span>
                    <span className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium"><a.icon className="h-4 w-4 text-slate-400" />{a.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {!loading && !term && (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="command-palette-hint">
                {t("search.hint")}
              </div>
            )}
            {!loading && term && groups.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="command-palette-empty">
                {t("search.empty")}
              </div>
            )}
            {!loading && groups.map((g) => {
              const Icon = ICONS[g.type];
              return (
                <CommandGroup key={g.type} heading={t(`search.${g.type}s`)}>
                  {g.items.map((it) => (
                    <CommandItem
                      key={`${g.type}-${it.id}`}
                      value={`${g.type}-${it.id}`}
                      onSelect={() => go(it.url)}
                      data-testid={`search-result-${g.type}`}
                      className="flex items-center gap-3"
                    >
                      {g.type === "product"
                        ? <ProductThumb path={it.image_url} alt={it.title} size={40} />
                        : (Icon && <Icon className="h-4 w-4 shrink-0 text-slate-400" />)}
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium">{it.title}</span>
                        {it.subtitle && <span className="truncate text-xs text-muted-foreground">{it.subtitle}</span>}
                      </div>
                      {it.selling_price && (
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{it.selling_price} €</span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
